import React, {useState,useEffect, useMemo, useRef, useCallback, memo} from 'react';
import './AnnotationTablePage.css'
import { useSelector, useDispatch } from 'react-redux'
import { selectCanAccess, selectCurrentUserId } from '../app/auth.js'
import { Navigate,useSearchParams } from 'react-router-dom';
import { 
    selectFilteredAnnotations, 
    selectAnnotationFields,
    allDescriptionKeys,
    annotationsApi,
    updateAnnotation,
    selectDynamicAnnotationFields,
    updateOneFieldOfDataFromKeys,
  } from '../app/annotations.js'
import { selectMultipleAnnotations, selectSelectedAnnotationIds, setMoveToAnnotationId } from '../app/interface.js'
import EditModal from '../Components/AnnotationEditionModal.jsx';
import { pos2InspectionCoordSys, selectCoordSys } from '../app/inspection.js';
import { ActivityTracker } from '../app/activity_tracking.js'
import apiClient from '../app/apiClient';
import {selectCanDo} from "../app/auth.js"
import { selectProjectId } from '../app/projects';
import { cleanCorruptedAnnotation } from "../app/ivone_utils.js";
import {
  ColumnFilterCheckboxListPanel,
  readNumberColumnFilterState,
  setColumnFilterAllValues,
  setColumnFilterOneValue,
} from '../Components/columnFilterCheckboxList.jsx';
import { TextColumnWithCheckboxListFilter } from '../Components/TextColumnWithCheckboxListFilter.jsx';
import { FILTER_POPOVER_CLEAR_BTN_STYLE } from '../Components/columnFilterPopoverStyles.js';
import { TableCellFieldInput } from '../Components/TableCellFieldInput.jsx';
import LabelTable from '../Components/LabelTable.jsx';

import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

/**
 * ANNOTATION TABLE PAGE - MEMORY & CACHE MANAGEMENT FEATURES
 * 
 * This component implements advanced performance optimizations to handle large datasets (22,000+ annotations)
 * through multiple caching and memory management strategies:
 * 
 * VIRTUALIZATION
 * - Renders only 50 visible rows instead of all annotations
 * - Virtual scrolling with calculated heights for non-rendered rows
 * - Constants: ROWS_DISPLAYED_ON_TOP (20), TOTAL_ROWS_DISPLAYED (50), ROW_HEIGHT_PIXELS (50)
 * 
 * DISPLAY DATA CACHE (displayDataCacheRef)
 * - Caches processed annotation data per annotation ID
 * - Stores: { displayData: cleanedData, annRef: annotation object ref (invalidates when Redux replaces the row) }
 * - Avoids expensive cleanCorruptedAnnotation() and pos2InspectionCoordSys() recalculations
 * - Cache invalidation: when Redux replaces the annotation row object (annRef !== cached) or list length changes
 * - SORTING OPTIMIZATION: Pre-populates cache for area/length columns before sorting to prevent coordinate transformations
 * 
 * COLUMN UNIQUE VALUES CACHE (columnUniqueValuesCacheRef)
 * - Caches unique values per column for filter dropdowns
 * - Cache key: `${columnName}_${annotations.length}` 
 * - Cleared when the filtered annotations list from Redux changes (same effect as display cache length invalidation)
 * - Calculated on-demand: only when filter is active OR popover is opened
 * 
 * SELECTION OPTIMIZATION (selectedAnnotationsIdsSet)
 * - Converts array to Set for O(1) lookups instead of O(n)
 * - Content-based comparison to avoid Set recreation with identical data
 * - Refs to maintain stable references: selectedAnnotationsIdsSetRef, selectedAnnotationsIdsStringRef
 * 
 * MEMOIZATION STRATEGY
 * - useMemo: all computed values (columns, filters, sorting, visible data)
 * - useCallback: all event handlers to prevent child re-renders
 * - React.memo: MemoizedTableRow component for individual row optimization
 * - NOTE: memo() = memoizes COMPONENTS (props change), useMemo() = memoizes VALUES (deps change)
 * 
 * PERFORMANCE METRICS
 * - DOM elements: virtualization
 * - Display data: cached per annotation (cleanCorruptedAnnotation expensive)
 * - Filter values: cached per column (calculated on-demand)
 * 
 * CACHE INVALIDATION TRIGGERS
 * - Display cache: annRef mismatch after edits, or full clear when annotation count changes
 * - Column unique-values cache: cleared when the annotations list from Redux changes reference
 * - Selection cache: selectedAnnotationIds array content change
 * 
 * REFS FOR PERFORMANCE
 * - annotationsToShowRef: avoid dependencies in handleRowClick
 * - lastSelectedRowIdxRef: Shift+click without re-render dependencies  
 * - columnsToDisplayRef: stable reference for callbacks
 * - selectedAnnotationsIdsSetRef: avoid useMemo dependencies
 */


// ============================================================================
// MEMOIZED COMPONENT
// ============================================================================
// Memoized table row component to prevent unnecessary re-renders
// Only re-renders if its props change (thanks to React.memo)
const MemoizedTableRow = memo(({ 
  ann, 
  annDisplayData, 
  isSelected, 
  columnsToDisplay, 
  onClick,
  rowHeight,
  onMoveTo,
  editingCell,
  editDraftValue,
  canEditCells,
  getFieldDefForColumn,
  onCellDoubleClick,
  onCellFieldChange,
  onCellEditEnter,
  onCellEditEscape,
}) => {
  const rowStyle = useMemo(() => ({
    overflow: "hidden",
    maxHeight: rowHeight,
    height: rowHeight,
    backgroundColor: isSelected ? "grey" : "white"
  }), [isSelected, rowHeight]);

  const cellStyle = useMemo(() => ({
    overflow: "hidden",
    whiteSpace: "normal",
    wordWrap: "break-word",
    padding: "8px",
  }), []);

  return (
    <TableRow 
      key={ann.id} 
      onClick={onClick} 
      style={rowStyle}
    >
      {columnsToDisplay.map((col) => {
        const fieldDef = canEditCells && !col.auto ? getFieldDefForColumn(ann, col.keyName) : null;
        const isEditing =
          editingCell?.annId === ann.id && editingCell?.columnKey === col.keyName;
        return (
          <TableCell
            key={col.keyName}
            data-cell-edit={isEditing ? '' : undefined}
            style={{
              ...cellStyle,
              cursor: fieldDef ? 'pointer' : undefined,
              padding: isEditing ? '4px' : cellStyle.padding,
            }}
            onMouseDown={(e) => {
              if (isEditing) e.stopPropagation();
            }}
            onClick={(e) => {
              if (isEditing) e.stopPropagation();
            }}
            onDoubleClick={(e) => {
              onCellDoubleClick(e, ann, col, fieldDef);
            }}
          >
            {isEditing && fieldDef ? (
              <TableCellFieldInput
                field={fieldDef}
                value={editDraftValue}
                onChange={onCellFieldChange}
                onEnter={onCellEditEnter}
                onEscape={onCellEditEscape}
              />
            ) : (
              annDisplayData[col.keyName]
            )}
          </TableCell>
        );
      })}
      <TableCell
        onClick={(e) => e.stopPropagation()}
        style={{ 
          padding: '8px',
          position: 'relative',
          zIndex: 10,
          pointerEvents: 'auto'
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (onMoveTo) {
              onMoveTo(ann.id);
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            display: 'flex',
            position: 'relative',
            zIndex: 11,
            pointerEvents: 'auto'
          }}
          title="Center view on this annotation"
        >
          <MyLocationIcon fontSize="small" />
        </button>
      </TableCell>
    </TableRow>
  );
});

MemoizedTableRow.displayName = 'MemoizedTableRow';

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function AnnotationTablePage(props) {
  const dispatch = useDispatch();

  // ============================================================================
  // SECTION 1: EXTERNAL DATA (Redux + URL)
  // ============================================================================
  // These values come from Redux or URL, they trigger re-renders when they change
  
  const [searchParams] = useSearchParams();
  const inspectionId = searchParams.get("inspectionId");
  
  // Redux selectors - global application data
  const viewKeys = useSelector(allDescriptionKeys);              // View configuration (available columns)
  const annotationFields = useSelector(selectAnnotationFields); // Annotation fields definition
  const selectedAnnotationsIds = useSelector(selectSelectedAnnotationIds); // Selected annotation IDs
  const annotations = useSelector(selectFilteredAnnotations);   // All annotations
  const selectedCordSys = useSelector(selectCoordSys);          // Coordinate system (for area/length)
  const currentUserId = useSelector(selectCurrentUserId); // Current user ID (for authorization)
  const canAccess = useSelector(selectCanAccess); // Can access (for edit modal)
  const canDo = useSelector(selectCanDo); // Can do (for edit modal)
  const projectId = useSelector(selectProjectId); // Project ID (for edit modal)
  const dynamicAnnotationFields = useSelector(selectDynamicAnnotationFields);
  const updateAnnotationsApi = useSelector(annotationsApi.update);
  const canEditAnnotationFields = canDo('createAnnotation', { projectId });

  // ============================================================================
  // SECTION 2: LOCAL STATE (useState)
  // ============================================================================
  // State that controls the UI and triggers re-renders
  
  // Columns
  const [columnsAvailable, setColumnsAvailable] = useState([]);  // All available columns
  const [columnsToDisplay, setColumnsToDisplay] = useState([]);  // Currently displayed columns
  
  // Filters
  const [columnFilters, setColumnFilters] = useState({});        // Active filters per column
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);    // Filter popover position
  const [currentFilterColumn, setCurrentFilterColumn] = useState(null); // Column being filtered
  const [forceRecalc, setForceRecalc] = useState(0);             // Force recalculation of unique values
  
  // Display
  const [showOnlySelected, setShowOnlySelected] = useState(false); // Show only selected annotations
  const [tableTab, setTableTab] = useState("annotations");
  const [scrollIdx, setScrollIdx] = useState(0);                  // Index of first visible row
  const [bottomHeight, setBottomHeight] = useState(0);            // Table container height
  
  // Sorting
  const [sortColumn, setSortColumn] = useState(null);            // Column key to sort by (null = no sort)
  const [sortDirection, setSortDirection] = useState('asc');      // 'asc' or 'desc'

  // Inline cell edit (click cell → edit → Enter → confirm → save to DB)
  const [editingCell, setEditingCell] = useState(null); // { annId, columnKey, columnTitle }
  const [editDraftValue, setEditDraftValue] = useState(undefined); // value of the cell being edited

  // ============================================================================
  // SECTION 3: REFS AND CACHES (useRef)
  // ============================================================================
  // Refs to store values without triggering re-renders
  // Caches to avoid expensive recalculations
  
  // DOM refs
  const topRef = useRef(null);              // HTML element above the table
  const containerRef = useRef(null);       // Table container (for scrolling)
  
  // Performance caches
  const displayDataCacheRef = useRef(new Map());           // Display data cache per annotation ID
  const annotationsVersionRef = useRef(annotations.length); // Annotations version (to invalidate cache)
  const columnUniqueValuesCacheRef = useRef(new Map());     // Unique values cache per column
  
  // Refs to avoid dependencies in callbacks
  const annotationsToShowRef = useRef([]);                  // Current annotationsToShow (for handleRowClick)
  // After inline save: keep row at same index while sort is active (until user changes sort)
  const pinnedRowAfterInlineSaveRef = useRef(null); // { annId, index }
  const lastSelectedRowIdxRef = useRef(null);              // Index of last selected row (for Shift+click)
  const columnsToDisplayRef = useRef([]);                  // Current columnsToDisplay
  const pendingRowClickTimerRef = useRef(null);            // Deferred row click (avoid select on dbl-click edit)
  const tracker = useRef(null);                            // Activity tracker

  // ============================================================================
  // SECTION 4: CONSTANTS
  // ============================================================================
  // Fixed values for virtualization and display
  
  const ROWS_DISPLAYED_ON_TOP = 20;  // Number of rows displayed above visible area
  const TOTAL_ROWS_DISPLAYED = 50;   // Total number of rows rendered in DOM
  const ROW_HEIGHT_PIXELS = 50;       // Row height in pixels
  const ROW_CLICK_DELAY_MS = 280;    // Wait before single-click selection (cancelled on double-click)

  // ============================================================================
  // SECTION 5: COMPUTED VALUES - SELECTION (useMemo)
  // ============================================================================
  // Selection optimization: array → Set conversion with content comparison
  // Avoids recreating Set if content is identical (same reference)
  
  const selectedAnnotationsIdsSetRef = useRef(new Set());
  const selectedAnnotationsIdsStringRef = useRef('');
  const selectedAnnotationsIdsSet = useMemo(() => {
    const idsString = selectedAnnotationsIds.join(',');
    // Compare content (string) rather than array reference
    if (idsString !== selectedAnnotationsIdsStringRef.current) {
      selectedAnnotationsIdsSetRef.current = new Set(selectedAnnotationsIds);
      selectedAnnotationsIdsStringRef.current = idsString;
    }
    return selectedAnnotationsIdsSetRef.current; // Always returns same reference if content identical
  }, [selectedAnnotationsIds]);

  // ============================================================================
  // SECTION 6: COMPUTED VALUES - COLUMNS (useMemo)
  // ============================================================================
  // Generate available columns (view columns + auto-generated columns)
  
  const allColumnsMemoized = useMemo(() => {
    // View columns
    let all_columns = annotationFields.keys.map((k) => ({
      title: k.title,
      keyName: k.name,
      field_type: k.type,
      auto: false
    }));
    
    // Auto-generated columns (area, length, geometry, created_by_email)
    const unit = selectedCordSys?.x?.unit || '';
    all_columns.push(
      { title: `Area (${unit}²)`, keyName: "__area__", field_type: "number", auto: true },
      { title: `Length (${unit})`, keyName: "__length__", field_type: "number", auto: true },
      { title: 'Geometry', keyName: "__geometry__", field_type: "text", auto: true },
      { title: 'Created by', keyName: "__created_by_email__", field_type: "text", auto: true },
    );
    return all_columns;
  }, [annotationFields.keys, selectedCordSys?.x?.unit]);

  // Initialize columns on load
  useEffect(() => {
    setColumnsToDisplay(allColumnsMemoized);
    setColumnsAvailable(allColumnsMemoized);
  }, [allColumnsMemoized]);

  // Displayed columns: optimized formats for lookups
  const columnsToDisplayKeyNames = useMemo(() => 
    columnsToDisplay.map((el) => el.keyName), 
    [columnsToDisplay]
  );
  const columnsToDisplayKeyNamesSet = useMemo(() => 
    new Set(columnsToDisplayKeyNames), 
    [columnsToDisplayKeyNames]
  );
  
  // Ref to avoid dependencies in callbacks
  useEffect(() => {
    columnsToDisplayRef.current = columnsToDisplay;
  }, [columnsToDisplay]);

  // ============================================================================
  // SECTION 7: COMPUTED VALUES - FILTERS (useMemo)
  // ============================================================================
  // Calculate unique values per column for checkbox/number filters
  // Only calculated if necessary (active filter or popover open)
  
  const columnUniqueValues = useMemo(() => {
    if (!annotations || annotations.length === 0) return {};
    const values = {};
    const annotationsVersion = annotations.length;
    
    columnsToDisplay.forEach(col => {
      const isTextInfoField = col.keyName === '__created_by_email__' ||
                              col.keyName === 'message';
      const useCheckboxFilter = (col.field_type === 'select' || col.keyName === '__geometry__') && !isTextInfoField;
      const useNumberFilter = col.field_type === 'number' && !isTextInfoField;
      const useTextColumnWithCheckboxList =
        (col.field_type === 'text' || col.field_type === 'textarea') &&
        !isTextInfoField &&
        col.keyName !== '__geometry__';
      
      if (useCheckboxFilter || useNumberFilter || useTextColumnWithCheckboxList) {
        const cacheKey = `${col.keyName}_${annotationsVersion}`;
        
        // Use cache if available
        if (columnUniqueValuesCacheRef.current.has(cacheKey)) {
          values[col.keyName] = columnUniqueValuesCacheRef.current.get(cacheKey);
          return;
        }
        
        // Calculate only if necessary
        const hasActiveFilter = columnFilters[col.keyName]?.type === 'checkbox' || columnFilters[col.keyName]?.type === 'number' ||
          (columnFilters[col.keyName]?.type === 'text' && (
            (columnFilters[col.keyName]?.valueSet && columnFilters[col.keyName].valueSet.size > 0) ||
            columnFilters[col.keyName]?.excludeAllColumnValues === true
          ));
        const isCurrentlyViewed = currentFilterColumn?.keyName === col.keyName;
        
        if (hasActiveFilter || isCurrentlyViewed) {
          const uniqueSet = new Set();
          
          if (col.keyName === '__geometry__') {
            // Optimization: direct access without cleanCorruptedAnnotation
            annotations.forEach(ann => {
              const geometryType = ann.geometry?.type === 'LineString' ? 'Line' : ann.geometry?.type;
              if (geometryType) uniqueSet.add(String(geometryType));
            });
          } else if (useNumberFilter) {
            annotations.forEach((ann) => {
              const displayData = cleanCorruptedAnnotation(viewKeys, ann, "display").data;
              if (selectedCordSys && (col.keyName === '__area__' || col.keyName === '__length__')) {
                const position = pos2InspectionCoordSys(selectedCordSys, ann.position);
                const autoValue = position[col.keyName.replace('__', '').replace('__', '')];
                if (autoValue?.value != null && !isNaN(autoValue.value)) {
                  uniqueSet.add(String(parseFloat(parseFloat(autoValue.value).toFixed(3))));
                }
              } else {
                const value = displayData[col.keyName];
                if (value != null && value !== 'N/A') {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) uniqueSet.add(String(numValue));
                }
              }
            });
          } else {
            annotations.forEach(ann => {
              const displayData = cleanCorruptedAnnotation(viewKeys, ann, "display").data;
              const value = displayData[col.keyName];
              if (value != null && value !== 'N/A') {
                uniqueSet.add(String(value));
              }
            });
          }
          
          const sortedValues = Array.from(uniqueSet).sort((a, b) => 
            useNumberFilter ? parseFloat(a) - parseFloat(b) : a.localeCompare(b)
          );
          values[col.keyName] = sortedValues;
          columnUniqueValuesCacheRef.current.set(cacheKey, sortedValues);
        } else if (columnUniqueValuesCacheRef.current.has(cacheKey)) {
          values[col.keyName] = columnUniqueValuesCacheRef.current.get(cacheKey);
        }
      }
    });
    return values;
  }, [annotations, columnsToDisplay, viewKeys, columnFilters, currentFilterColumn, filterAnchorEl, forceRecalc, selectedCordSys]);

  // ============================================================================
  // SECTION 8: COMPUTED VALUES - ANNOTATION FILTERING (useMemo)
  // ============================================================================
  // Apply column filters to annotations
  // Uses displayDataCacheRef cache to avoid recalculations
  
  const filteredAnnotations = useMemo(() => {
    if (!annotations || annotations.length === 0) return [];
    
    // Active filters
    const activeFilters = Object.entries(columnFilters).filter(([_, filter]) => 
      filter && 
      ((filter.type === 'text' && (
        (filter.value && String(filter.value).trim()) ||
        filter.excludeAllColumnValues === true ||
        (filter.allowedValues && filter.allowedValues.size > 0) ||
        (filter.valueSet && filter.valueSet.size > 0)
      )) || 
       (filter.type === 'checkbox' && (
         filter.excludeAllColumnValues === true ||
         (filter.allowedValues?.size ?? filter.value?.size ?? 0) > 0
       )) ||
       (filter.type === 'number' && (
         (filter.value?.compareValue !== undefined && filter.value?.compareValue !== '') ||
         (filter.value?.value !== undefined && filter.value?.value !== '') ||
         filter.value?.excludeAllColumnValues === true ||
         filter.value?.discreteNone === true ||
         (filter.value?.allowedValues && filter.value.allowedValues.size > 0) ||
         (filter.value?.size && filter.value.size.size > 0)
       )))
    );
    
    if (activeFilters.length === 0) return annotations;
    
    // Build filter functions
    const filterFunctions = activeFilters.map(([columnKey, filter]) => {
      const column = columnsToDisplay.find(col => col.keyName === columnKey);
      if (!column) return null;
      
      // Helper: get displayData (from cache or calculation)
      const getDisplayData = (ann) => {
        const cacheKey = ann.id;
        if (displayDataCacheRef.current.has(cacheKey)) {
          const cached = displayDataCacheRef.current.get(cacheKey);
          if (cached.annRef === ann) return cached.displayData;
        }
        const ann_to_display = cleanCorruptedAnnotation(viewKeys, ann, "display");
        const displayData = ann_to_display.data;
        
        // Add auto-generated fields
        if (selectedCordSys) {
          const position = pos2InspectionCoordSys(selectedCordSys, ann.position);
          Object.entries(position).forEach(([k, v]) => {
            if (k === "area" || k === "length") {
              displayData[`__${k}__`] = parseFloat(parseFloat(position[k].value).toFixed(3));
            }
          });
        }
        displayData["__geometry__"] = ann.geometry.type === "LineString" ? "Line" : ann.geometry.type;
        displayData["__created_by_email__"] = ann.createdByEmail || ann.createdBy || '';
        
        return displayData;
      };
      
      // Text filter (optional substring + optional checkbox list on column values)
      if (filter.type === 'text') {
        const q = (filter.value && String(filter.value).trim().toLowerCase()) || '';
        return (ann) => {
          const displayData = getDisplayData(ann);
          const cellStr = String(displayData[columnKey] || '');
          const matchSearch = !q || cellStr.toLowerCase().includes(q);
          if (filter.excludeAllColumnValues || filter.emptyDiscrete) return false;
          const allowed =
            filter.allowedValues?.size > 0 ? filter.allowedValues : filter.valueSet;
          if (allowed && allowed.size > 0) {
            if (!allowed.has(cellStr)) return false;
          }
          return matchSearch;
        };
      }
      
      // Checkbox filter (select/geometry)
      if (filter.type === 'checkbox' && (column.field_type === 'select' || column.keyName === '__geometry__')) {
        if (filter.excludeAllColumnValues || filter.emptyDiscrete) {
          return () => false;
        }
        const allowedValues = filter.allowedValues || filter.value;
        return (ann) => {
          const displayData = getDisplayData(ann);
          return allowedValues.has(String(displayData[columnKey] || ''));
        };
      }
      
      // Number filter (operators + checkboxes)
      if (filter.type === 'number') {
        return (ann) => {
          const displayData = getDisplayData(ann);
          const cellValue = displayData[columnKey];
          const numValue = parseFloat(cellValue);
          
          if (isNaN(numValue)) return false;
          
          // Comparison operator
          let matchesOperator = true;
          const threshold =
            filter.value.compareValue ?? filter.value.value;
          if (threshold !== undefined && threshold !== '') {
            const filterValue = parseFloat(threshold);
            if (!isNaN(filterValue)) {
              const operator = filter.value.operator || '=';
              switch (operator) {
                case '>': matchesOperator = numValue > filterValue; break;
                case '<': matchesOperator = numValue < filterValue; break;
                case '>=': matchesOperator = numValue >= filterValue; break;
                case '<=': matchesOperator = numValue <= filterValue; break;
                case '=': matchesOperator = numValue === filterValue; break;
              }
            }
          }
          
          // Checkboxes (subset of listed values)
          let matchesCheckbox = true;
          if (filter.value?.excludeAllColumnValues || filter.value?.discreteNone) {
            matchesCheckbox = false;
          } else if (filter.value?.allowedValues?.size > 0) {
            matchesCheckbox = filter.value.allowedValues.has(String(cellValue || ''));
          } else if (filter.value?.size && filter.value.size.size > 0) {
            matchesCheckbox = filter.value.size.has(String(cellValue || ''));
          }
          
          return matchesOperator && matchesCheckbox;
        };
      }
      
      return null;
    }).filter(fn => fn !== null);
    
    // Apply all filters
    return annotations.filter(ann => filterFunctions.every(filterFn => filterFn(ann)));
  }, [annotations, columnFilters, columnsToDisplay, viewKeys, selectedCordSys]);

  // ============================================================================
  // SECTION 9: COMPUTED VALUES - ANNOTATIONS TO SHOW (useMemo)
  // ============================================================================
  // Additional filter: show only selected annotations
  // Uses selectedAnnotationsIdsSetRef.current to avoid dependency
  // Also applies sorting if a sort column is selected
  
  const annotationsToShow = useMemo(() => {
    if (!filteredAnnotations || filteredAnnotations.length === 0) return [];
    
    let result = filteredAnnotations;
    
    // Filter by selection if needed
    if (showOnlySelected) {
      result = result.filter((ann) => selectedAnnotationsIdsSetRef.current.has(ann.id));
    }
    
  // Apply sorting if a column is selected
  if (sortColumn) {
    const column = columnsToDisplay.find(col => col.keyName === sortColumn);
    if (column) {
      // ========================================================================
      // PERFORMANCE OPTIMIZATION FOR AREA/LENGTH SORTING
      // ========================================================================
      // Pre-calculate and cache ALL display data for annotations BEFORE sorting
      // This avoids expensive operations during sort comparisons:
      // - cleanCorruptedAnnotation() data cleaning
      // - pos2InspectionCoordSys() coordinate transformations 
      // - Auto-generated columns calculation (area, length, geometry, created_by_email)
      if ((sortColumn === '__area__' || sortColumn === '__length__') && selectedCordSys) {
        result.forEach(ann => {
          const cacheKey = ann.id;
          // Only calculate if not in cache or annotation was updated
          if (!displayDataCacheRef.current.has(cacheKey) || 
              displayDataCacheRef.current.get(cacheKey).annRef !== ann) {
            
            // Clean corrupted annotation data
            let ann_to_display = cleanCorruptedAnnotation(viewKeys, ann, "display");
            
            // Expensive coordinate transformation (only done once per annotation now)
            const position = pos2InspectionCoordSys(selectedCordSys, ann.position);
            
            // Extract area and length values from coordinate transformation
            Object.entries(position).forEach(([k, v]) => {
              if (k === "area" || k === "length") {
                // Check if position[k] exists and has a valid value
                if (position[k] && position[k].value != null && !isNaN(position[k].value)) {
                ann_to_display.data[`__${k}__`] = parseFloat(parseFloat(position[k].value).toFixed(3));
                } else {
                  // If area/length is null/undefined (degenerate geometry), set to 0
                  ann_to_display.data[`__${k}__`] = 0;
                }
              }
            });
            
            // Add other auto-generated columns
            ann_to_display.data["__geometry__"] = ann.geometry.type === "LineString" ? "Line" : ann.geometry.type;
            ann_to_display.data["__created_by_email__"] = ann.createdByEmail;
            
            // Cache the computed values for future use
            displayDataCacheRef.current.set(cacheKey, {
              displayData: ann_to_display.data,
              annRef: ann
            });
          }
        });
      }
      
      // Create a sorted copy using JavaScript's native sort
      result = [...result].sort((a, b) => {
        // Extract value for comparison from each annotation
        const getDisplayValue = (ann) => {
          const cacheKey = ann.id;
          
          // Try to get value from cache first (O(1) lookup)
          if (displayDataCacheRef.current.has(cacheKey)) {
            const cached = displayDataCacheRef.current.get(cacheKey);
            // Verify cache is still valid (Redux replaces the annotation object on update)
            if (cached.annRef === ann) {
              return cached.displayData[sortColumn];
            }
          }
          
          // Fallback: calculate if not in cache (rare after pre-population optimization)
          let ann_to_display = cleanCorruptedAnnotation(viewKeys, ann, "display");
          
          // Add auto-generated columns if needed
          if (selectedCordSys) {
            const position = pos2InspectionCoordSys(selectedCordSys, ann.position);
            Object.entries(position).forEach(([k, v]) => {
              if (k === "area" || k === "length") {
                ann_to_display.data[`__${k}__`] = parseFloat(parseFloat(position[k].value).toFixed(3));
              }
            });
          }
          ann_to_display.data["__geometry__"] = ann.geometry.type === "LineString" ? "Line" : ann.geometry.type;
          ann_to_display.data["__created_by_email__"] = ann.createdByEmail;
          
          // Cache the computed result for future use
          displayDataCacheRef.current.set(cacheKey, {
            displayData: ann_to_display.data,
            annRef: ann
          });
          
          return ann_to_display.data[sortColumn];
        };
        
        // Get values to compare (should be fast now thanks to caching)
        const valueA = getDisplayValue(a);
        const valueB = getDisplayValue(b);
        
        // Handle null/undefined values (put them at the end)
        if (valueA == null && valueB == null) return 0;
        if (valueA == null) return 1;  // null values go to bottom
        if (valueB == null) return -1; // null values go to bottom
        
        // Numeric comparison for number fields (area, length, etc.)
        if (column.field_type === 'number' || sortColumn === '__area__' || sortColumn === '__length__') {
          const numA = parseFloat(valueA);
          const numB = parseFloat(valueB);
          if (!isNaN(numA) && !isNaN(numB)) {
            return sortDirection === 'asc' ? numA - numB : numB - numA;
          }
        }
        
        // String comparison for text fields (case-insensitive)
        const strA = String(valueA).toLowerCase();
        const strB = String(valueB).toLowerCase();
        if (sortDirection === 'asc') {
          return strA.localeCompare(strB);
        } else {
          return strB.localeCompare(strA);
        }
      });
    }
  }

    const pin = pinnedRowAfterInlineSaveRef.current;
    if (pin && sortColumn) {
      const pinnedAnn = result.find((a) => a.id === pin.annId);
      if (pinnedAnn) {
        const without = result.filter((a) => a.id !== pin.annId);
        const insertAt = Math.min(pin.index, without.length);
        without.splice(insertAt, 0, pinnedAnn);
        result = without;
      }
    }

    return result;
  }, [filteredAnnotations, showOnlySelected, sortColumn, sortDirection, columnsToDisplay, viewKeys, selectedCordSys]);

  // Ref to avoid dependencies in handleRowClick
  useEffect(() => {
    annotationsToShowRef.current = annotationsToShow;
  }, [annotationsToShow]);

  // Map for O(1) lookup: annotation ID → index in annotationsToShow
  const annotationsToShowIndexMap = useMemo(() => {
    const map = new Map();
    annotationsToShow.forEach((ann, idx) => map.set(ann.id, idx));
    return map;
  }, [annotationsToShow]);

  // ============================================================================
  // SECTION 10: COMPUTED VALUES - VIRTUALIZATION (useMemo)
  // ============================================================================
  // Virtualization: only display visible rows + buffer
  // Reduces number of DOM elements (max 50 rows displayed at a time)
  
  const visibleAnnotations = useMemo(() => {
    if (!annotationsToShow || annotationsToShow.length === 0) return [];
    return annotationsToShow.slice(scrollIdx, scrollIdx + TOTAL_ROWS_DISPLAYED);
  }, [annotationsToShow, scrollIdx]);

  // Calculate virtual row heights (before/after)
  const beforeCount = useMemo(() => scrollIdx, [scrollIdx]);
  const afterCount = useMemo(() => 
    Math.max(0, annotationsToShow.length - scrollIdx - TOTAL_ROWS_DISPLAYED), 
    [annotationsToShow.length, scrollIdx]
  );
  const beforeRowStyle = useMemo(() => 
    ({ height: beforeCount * ROW_HEIGHT_PIXELS }), 
    [beforeCount]
  );
  const afterRowStyle = useMemo(() => 
    ({ height: afterCount * ROW_HEIGHT_PIXELS }), 
    [afterCount]
  );

  // ============================================================================
  // SECTION 11: COMPUTED VALUES - DISPLAY DATA (useMemo)
  // ============================================================================
  // Pre-calculate display data for visible rows
  // Uses displayDataCacheRef cache to avoid recalculations
  // Also calculates isSelected for each row
  
  const visibleRowsData = useMemo(() => {
    if (!visibleAnnotations || visibleAnnotations.length === 0) return [];
    
    return visibleAnnotations.map((ann) => {
      const cacheKey = ann.id;
      
      // Check cache
      if (displayDataCacheRef.current.has(cacheKey)) {
        const cached = displayDataCacheRef.current.get(cacheKey);
        if (cached.annRef === ann) {
          return {
            ann,
            displayData: cached.displayData,
            isSelected: selectedAnnotationsIdsSet.has(ann.id)
          };
        }
      }

      // Calculate display data
      let ann_to_display = cleanCorruptedAnnotation(viewKeys, ann, "display");
      
      // Add auto-generated columns
      if (selectedCordSys) {
        const position = pos2InspectionCoordSys(selectedCordSys, ann.position);
        Object.entries(position).forEach(([k, v]) => {
          if (k === "area" || k === "length") {
            ann_to_display.data[`__${k}__`] = parseFloat(parseFloat(position[k].value).toFixed(3));
          }
        });
      }
      ann_to_display.data["__geometry__"] = ann.geometry.type === "LineString" ? "Line" : ann.geometry.type;
      ann_to_display.data["__created_by_email__"] = ann.createdByEmail;
      
      // Cache result
      displayDataCacheRef.current.set(cacheKey, {
        displayData: ann_to_display.data,
        annRef: ann
      });
      
      return {
        ann,
        displayData: ann_to_display.data,
        isSelected: selectedAnnotationsIdsSet.has(ann.id)
      };
    });
  }, [visibleAnnotations, viewKeys, selectedCordSys, selectedAnnotationsIdsSet]);

  // ============================================================================
  // SECTION 12: EVENT HANDLERS (useCallback)
  // ============================================================================
  // Memoized handlers to avoid unnecessary re-renders of child components
  
  // Selection
  const SelectAll = useCallback(() => {
    const ids = annotationsToShow.map(ann => ann.id);
    dispatch(selectMultipleAnnotations(ids));
  }, [annotationsToShow, dispatch]);

  const UnselectAll = useCallback(() => {
    dispatch(selectMultipleAnnotations([]));
  }, [dispatch]);

  const toggleShowOnlySelected = useCallback(() => {
    setShowOnlySelected(prev => !prev);
  }, []);

  const selectionIncludesId = (ids, id) =>
    ids.some((selectedId) => String(selectedId) === String(id));

  // Row click: handles simple click, Shift+click, Ctrl+click
  const handleRowClick = useCallback((event, clickedAnnotationId, rowIdx) => {
    if (editingCell) {
      return;
    }
    const isShiftPressed = event.shiftKey;
    const isCtrlPressed = event.ctrlKey || event.metaKey;
    const clickedId = String(clickedAnnotationId);
    
    const currentAnnotationsToShow = annotationsToShowRef.current;
    const currentSelectedIds = selectedAnnotationsIds;
    const currentSelectedSet = new Set(currentSelectedIds.map(String));
    
    let newSelection;
    
    if (isShiftPressed && lastSelectedRowIdxRef.current !== null && currentSelectedSet.size > 0) {
      // Range selection (Shift+click)
      const startIdx = Math.min(lastSelectedRowIdxRef.current, rowIdx);
      const endIdx = Math.max(lastSelectedRowIdxRef.current, rowIdx);
      const rangeIds = currentAnnotationsToShow.slice(startIdx, endIdx + 1).map(ann => ann.id);
      const allRangeSelected = rangeIds.every(id => currentSelectedSet.has(String(id)));
      
      const currentSelectionSet = new Set(currentSelectedIds);
      if (allRangeSelected) {
        rangeIds.forEach(id => currentSelectionSet.delete(id)); // Deselect if all selected
      } else {
        rangeIds.forEach(id => currentSelectionSet.add(id)); // Select
      }
      newSelection = Array.from(currentSelectionSet);
      lastSelectedRowIdxRef.current = rowIdx;
    } else if (isCtrlPressed) {
      // Multi-select (Ctrl+click) — toggle off only with Ctrl
      if (selectionIncludesId(currentSelectedIds, clickedId)) {
        newSelection = currentSelectedIds.filter((id) => String(id) !== clickedId);
      } else {
        newSelection = [...currentSelectedIds, clickedAnnotationId];
      }
      lastSelectedRowIdxRef.current = rowIdx;
    } else {
      // Simple click: unselected row → select only it; sole selected row → clear; multi → keep selection
      if (selectionIncludesId(currentSelectedIds, clickedId)) {
        if (currentSelectedIds.length === 1) {
          newSelection = [];
          lastSelectedRowIdxRef.current = null;
        } else {
          lastSelectedRowIdxRef.current = rowIdx;
          return;
        }
      } else {
        newSelection = [clickedAnnotationId];
        lastSelectedRowIdxRef.current = rowIdx;
      }
    }
    
    // Dispatch only if selection actually changed
    if (newSelection.length !== currentSelectedIds.length || 
        !newSelection.every(id => selectionIncludesId(currentSelectedIds, id))) {
      dispatch(selectMultipleAnnotations(newSelection));
    }
  }, [dispatch, selectedAnnotationsIds, editingCell]);

  const clearPendingRowClick = useCallback(() => {
    if (pendingRowClickTimerRef.current != null) {
      clearTimeout(pendingRowClickTimerRef.current);
      pendingRowClickTimerRef.current = null;
    }
  }, []);

  const handleRowClickDeferred = useCallback((event, clickedAnnotationId, rowIdx) => {
    clearPendingRowClick();
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      handleRowClick(event, clickedAnnotationId, rowIdx);
      return;
    }
    const { shiftKey, ctrlKey, metaKey } = event;
    pendingRowClickTimerRef.current = setTimeout(() => {
      pendingRowClickTimerRef.current = null;
      handleRowClick(
        { shiftKey, ctrlKey, metaKey },
        clickedAnnotationId,
        rowIdx
      );
    }, ROW_CLICK_DELAY_MS);
  }, [clearPendingRowClick, handleRowClick]);

  const getFieldDefForAnnotation = (ann, columnKey) => {
    const keys = dynamicAnnotationFields(ann.data).keys;
    return keys.find((k) => k.name === columnKey) ?? null;
  };

  const cancelCellEdit = () => {
    setEditingCell(null);
    setEditDraftValue(undefined);
  };
  const cancelCellEditRef = useRef(cancelCellEdit);
  cancelCellEditRef.current = cancelCellEdit;

  const handleCellEditStart = (ann, col) => {
    if (!canEditAnnotationFields || col.auto) return;
    const fieldDef = getFieldDefForAnnotation(ann, col.keyName);
    if (!fieldDef) return;
    const rawData = cleanCorruptedAnnotation(viewKeys, ann, 'default').data;
    const originalValue = rawData[col.keyName];
    setEditingCell({
      annId: ann.id,
      columnKey: col.keyName,
      columnTitle: col.title,
      originalValue,
    });
    setEditDraftValue(originalValue);
  };

  const buildCellSavePayload = () => {
    if (!editingCell || editDraftValue === undefined) return null;
    const ann = annotations.find((a) => a.id === editingCell.annId);
    if (!ann) return null;
    const baseData = cleanCorruptedAnnotation(viewKeys, ann, 'default').data;
    const { updatedData } = updateOneFieldOfDataFromKeys(
      viewKeys,
      baseData,
      editingCell.columnKey,
      editDraftValue
    );
    const previousDisplay = cleanCorruptedAnnotation(viewKeys, ann, 'display').data[
      editingCell.columnKey
    ];
    const newDisplay = cleanCorruptedAnnotation(
      viewKeys,
      { ...ann, data: updatedData },
      'display'
    ).data[editingCell.columnKey];
    return { ann, baseData, updatedData, previousDisplay, newDisplay };
  };

  const commitCellEdit = () => {
    const payload = buildCellSavePayload();
    if (!payload) {
      cancelCellEdit();
      return;
    }
    const { ann, baseData, updatedData, previousDisplay, newDisplay } = payload;
    const unchanged =
      String(previousDisplay ?? '') === String(newDisplay ?? '') &&
      baseData[editingCell.columnKey] === updatedData[editingCell.columnKey];
    if (unchanged) {
      cancelCellEdit();
      return;
    }
    if (!inspectionId) return;

    const snapshotAnn = { ...ann, data: { ...baseData } };
    const optimisticAnn = { ...ann, data: updatedData };
    const rowIndex = annotationsToShowRef.current.findIndex((a) => a.id === ann.id);
    if (rowIndex >= 0) {
      pinnedRowAfterInlineSaveRef.current = { annId: ann.id, index: rowIndex };
    }
    dispatch(updateAnnotation(optimisticAnn));
    displayDataCacheRef.current.delete(ann.id);
    cancelCellEdit();

    updateAnnotationsApi(
      { ids: [ann.id], data: updatedData, inspectionId },
      (result) => {
        if (result !== null) {
          dispatch(updateAnnotation(result[0]));
          displayDataCacheRef.current.delete(ann.id);
        } else {
          dispatch(updateAnnotation(snapshotAnn));
          displayDataCacheRef.current.delete(ann.id);
          alert('Failed to save attribute change. The previous value was restored.');
        }
      }
    ).catch((err) => {
      dispatch(updateAnnotation(snapshotAnn));
      displayDataCacheRef.current.delete(ann.id);
      const message = err?.message || 'Unknown error';
      alert(`Error saving attribute change: ${message}`);
    });
  };
  const commitCellEditRef = useRef(commitCellEdit);
  commitCellEditRef.current = commitCellEdit;

  const handleCellDoubleClick = (e, ann, col, fieldDef) => {
    clearPendingRowClick();
    if (!fieldDef) return;
    e.stopPropagation();
    handleCellEditStart(ann, col);
  };

  useEffect(() => clearPendingRowClick, [clearPendingRowClick]);

  useEffect(() => {
    if (!editingCell) return undefined;
    const onDocumentMouseDown = (event) => {
      if (event.target.closest('[data-cell-edit]')) return;
      if (event.target.closest('.MuiPopover-root')) return;
      if (event.target.closest('.MuiPopper-root')) return;
      commitCellEditRef.current();
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [editingCell]);

  // MUI Select menu captures Escape before cell handlers — listen in capture phase
  useEffect(() => {
    if (!editingCell) return undefined;
    const onDocumentKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      cancelCellEditRef.current();
    };
    document.addEventListener('keydown', onDocumentKeyDown, true);
    return () => document.removeEventListener('keydown', onDocumentKeyDown, true);
  }, [editingCell]);

  // Navigation
  const handleMoveTo = useCallback((annotationId) => {
    dispatch(setMoveToAnnotationId(annotationId));
  }, [dispatch]);

  // Columns
  const handleToggleColumn = useCallback((event) => {
    setColumnsToDisplay(columnsAvailable.filter((col) => event.target.value.includes(col.keyName)));
  }, [columnsAvailable]);

  // Sorting: handle column header click
  const handleSort = useCallback((columnKey) => {
    if (sortColumn === columnKey) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column: start with ascending
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  // ============================================================================
  // SECTION 13: EFFECTS (useEffect)
  // ============================================================================
  // Side effects: initialization, cleanup, synchronization
  
  // Force recalculation of unique values when filter popover opens (column header filter button)
  useEffect(() => {
    if (filterAnchorEl && currentFilterColumn) {
      setForceRecalc(prev => prev + 1);
    }
  }, [filterAnchorEl, currentFilterColumn]);

  // Invalidate caches when the annotations list changes (count or row objects from Redux)
  useEffect(() => {
    if (annotations.length !== annotationsVersionRef.current) {
      displayDataCacheRef.current.clear();
      annotationsVersionRef.current = annotations.length;
    }
    columnUniqueValuesCacheRef.current.clear();
  }, [annotations]);

  // Update lastSelectedRowIdxRef when selection changes (from outside) (Shift+click, Ctrl+click, etc.)
  useEffect(() => {
    if (selectedAnnotationsIds.length === 1) {
      const selectedId = selectedAnnotationsIds[0];
      const idx = annotationsToShowIndexMap.get(selectedId);
      if (idx !== undefined) {
        lastSelectedRowIdxRef.current = idx;
      }
    } else if (selectedAnnotationsIds.length === 0) {
      lastSelectedRowIdxRef.current = null;
    }
  }, [selectedAnnotationsIds, annotationsToShowIndexMap]);

  // Reset scroll when list size / filters / sort change — not on a single inline field update
  useEffect(() => {
    pinnedRowAfterInlineSaveRef.current = null;
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [annotations.length, showOnlySelected, sortColumn, sortDirection]);

  // Calculate table container height (to set the height of the table container)
  useEffect(() => {
    const updateHeight = () => {
      if (topRef.current) {
        const rect = topRef.current.getBoundingClientRect();
        setBottomHeight(window.innerHeight - rect.bottom);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Handle scroll: update scrollIdx for virtualization (to display the correct rows)
  // (when the user scrolls, we need to update the scrollIdx to display the correct rows)
  useEffect(() => {
    if (!containerRef.current) return;
    
    const handleScroll = () => {
      const clamp = (min, value, max) => Math.max(min, Math.min(value, max));
      setScrollIdx((prev) => {
        if (!containerRef.current) return prev;
        const estimated_first_visible = Math.floor(containerRef.current.scrollTop / ROW_HEIGHT_PIXELS);
        const new_offset = clamp(0, estimated_first_visible - ROWS_DISPLAYED_ON_TOP, annotationsToShow.length - TOTAL_ROWS_DISPLAYED);
        // Hysteresis: only change if significant difference (avoids infinite loops)
        return Math.abs(new_offset - prev) > 1 ? new_offset : prev;
      });
    };
    
    containerRef.current.addEventListener("scroll", handleScroll);
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [annotationsToShow.length]);

  // Activity tracking
  useEffect(() => {
    if (currentUserId != null && tracker.current === null) {
      tracker.current = new ActivityTracker({
        metadata: {},
        event_min_duration: 2,
        sending_frequency: 15,
        sending_function: async (infos, events) => {
          return apiClient.create_user_event({
            userId: currentUserId,
            category: "activity_tracking",
            other_infos: {
            ...infos,
            inspectionId: inspectionId,
            component: "AttributeTable",
            channel: window.orthophoto_bc_channel,
            events: events
          }
          }); 
        }
      });
      tracker.current.start_tracking_html_element(document.body);
    }
  }, [currentUserId, inspectionId]);

  useEffect(() => {
    return () => {
      if (tracker.current != null) {
        tracker.current.stop_tracking_html_element();
        tracker.current = null;
      }
    };
  }, []);

  // ============================================================================
  // SECTION 14: MEMOIZED STYLES (useMemo)
  // ============================================================================
  // Styles calculated once and reused
  
  // Memo for optimal performance (to avoid unnecessary re-renders)
  const tableContainerStyle = useMemo(() => 
    ({ height: bottomHeight, overflowY: "auto" }), 
    [bottomHeight]
  );

  // ============================================================================
  // SECTION 15: RENDERING
  // ============================================================================
  
  // Helper: format active filter label (to display the active filters in the header)
  // Memoized to avoid recreating on every render
  const getFilterLabel = useCallback((column, filter) => {
    // Helper: format values list (used by checkbox and number filters)
    const formatValuesList = (valueSet) => {
      if (!valueSet || valueSet.size === 0) return '';
      const values = Array.from(valueSet).slice(0, 2);
      const moreCount = valueSet.size - 2;
      return values.join(', ') + (moreCount > 0 ? ` +${moreCount}` : '');
    };

    if (filter.type === 'text') {
      const parts = [];
      if (filter.value && String(filter.value).trim()) {
        parts.push(`"${filter.value}"`);
      }
      if (filter.excludeAllColumnValues || filter.emptyDiscrete) {
        parts.push('(aucune valeur)');
      } else if (filter.allowedValues?.size > 0) {
        parts.push(formatValuesList(filter.allowedValues));
      } else if (filter.valueSet && filter.valueSet.size > 0) {
        parts.push(formatValuesList(filter.valueSet));
      }
      return parts.length > 0 ? `${column.title}: ${parts.join(' | ')}` : '';
    }
    
    if (filter.type === 'checkbox') {
      if (filter.excludeAllColumnValues || filter.emptyDiscrete) {
        return `${column.title}: (aucune valeur)`;
      }
      const allowed = filter.allowedValues || filter.value;
      if (allowed?.size > 0) {
        return `${column.title}: ${formatValuesList(allowed)}`;
      }
    }

    if (filter.type === 'number') {
      const parts = [];
      const operatorSymbols = { '>': '>', '<': '<', '>=': '≥', '<=': '≤', '=': '=' };
      
      const threshold = filter.value?.compareValue ?? filter.value?.value;
      if (threshold) {
        parts.push(`${operatorSymbols[filter.value.operator || '>']} ${threshold}`);
      }
      if (filter.value?.excludeAllColumnValues || filter.value?.discreteNone) {
        parts.push('valeurs: aucune');
      } else if (filter.value?.allowedValues?.size > 0) {
        parts.push(formatValuesList(filter.value.allowedValues));
      } else if (filter.value?.size?.size > 0) {
        parts.push(formatValuesList(filter.value.size));
      }
      return parts.length > 0 ? `${column.title}: ${parts.join(' | ')}` : '';
    }
    
    return '';
  }, []); // No dependencies: pure function

  // Helper: check if a filter has an active value (to display the active filters in the header)
  // Memoized to avoid recreating on every render
  const hasActiveFilterValue = useCallback((filter) => {
    if (!filter) return false;
    if (filter.type === 'text' && (
      (filter.value && String(filter.value).trim()) ||
      filter.excludeAllColumnValues === true ||
      filter.emptyDiscrete === true ||
      (filter.allowedValues && filter.allowedValues.size > 0) ||
      (filter.valueSet && filter.valueSet.size > 0)
    )) return true;
    if (filter.type === 'checkbox' && (
      filter.excludeAllColumnValues === true ||
      filter.emptyDiscrete === true ||
      (filter.allowedValues && filter.allowedValues.size > 0) ||
      (filter.value && filter.value.size > 0)
    )) return true;
    if (filter.type === 'number' && filter.value && 
        ((filter.value.compareValue !== undefined && filter.value.compareValue !== '') ||
         (filter.value.value !== undefined && filter.value.value !== '') || 
         filter.value.excludeAllColumnValues === true ||
         filter.value.discreteNone === true ||
         (filter.value.allowedValues && filter.value.allowedValues.size > 0) ||
         (filter.value.size && filter.value.size.size > 0))) return true;
    return false;
  }, []); // No dependencies: pure function

  // Permission check: redirect if user doesn't have access (after all hooks to respect rules of hooks)
  if (projectId && !canAccess('AnnotationTablePage', {projectId})) {
    return <Navigate to="/" replace />;
  }
 

  return (  
    <div className="main">
      <div className="headers">
        <button
          type="button"
          style={{ fontWeight: tableTab === "annotations" ? 700 : 400 }}
          onClick={() => {
            setTableTab("annotations");
            dispatch(selectMultipleAnnotations([]));
          }}
        >
          Annotations
        </button>
        <button
          type="button"
          style={{ fontWeight: tableTab === "labels" ? 700 : 400 }}
          onClick={() => {
            setTableTab("labels");
            dispatch(selectMultipleAnnotations([]));
          }}
        >
          Labels
        </button>
      </div>
      {tableTab === "labels" ? <LabelTable /> : (
      <>
      {/* Header: selection and filters */}
      <div className="headers" ref={topRef}>
        <p>
          <b>Selection</b>:&nbsp;{selectedAnnotationsIds.length} annotations selected 
          &nbsp;{annotationsToShow.length > 0 && <button onClick={SelectAll}>Select all</button>}&nbsp;
          &nbsp;<button onClick={UnselectAll}>Unselect all</button>&nbsp;
          &nbsp;<button onClick={toggleShowOnlySelected}>
              {showOnlySelected ? "Show All" : "Show Selection"}
          </button>&nbsp;
        </p>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {/* Column selection */}
          <Select
            multiple
            value={columnsToDisplayKeyNames}
            renderValue={() => "Columns"}
            onChange={handleToggleColumn}
          >
            {columnsAvailable.map((column) => (
              <MenuItem key={column.keyName} value={column.keyName}>
                <Checkbox checked={columnsToDisplayKeyNamesSet.has(column.keyName)} />
                <ListItemText primary={column.title} />
              </MenuItem>
            ))}
          </Select>
          
          {/* Active filters list */}
          {Object.keys(columnFilters).length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Active filters:
              </Typography>
              {Object.entries(columnFilters).map(([columnKey, filter]) => {
                const column = columnsToDisplay.find(col => col.keyName === columnKey);
                if (!column || !hasActiveFilterValue(filter)) return null;
                
                const filterLabel = getFilterLabel(column, filter);
                if (!filterLabel) return null;
                
                return (
                  <Box
                    key={columnKey}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      padding: '4px 8px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <span>{filterLabel}</span>
                    <button
                      onClick={() => {
                        setColumnFilters(prev => {
                          const newFilters = { ...prev };
                          delete newFilters[columnKey];
                          return newFilters;
                        });
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0 4px',
                        fontSize: '14px',
                        color: '#666',
                        lineHeight: 1
                      }}
                      title="Remove this filter"
                    >
                      ×
                    </button>
                  </Box>
                );
              })}
              
              {/* "Clean filters" button */}
              <button
                onClick={() => setColumnFilters({})}
                style={{
                  padding: '4px 12px',
                  background: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clean filters
              </button>
            </Box>
          )}
        </div>
      </div>

      {/* Table */}
      <TableContainer ref={containerRef} component={Paper} style={tableContainerStyle}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columnsToDisplay.map((col) => {
                const hasFilter = hasActiveFilterValue(columnFilters[col.keyName]);
                const isSorted = sortColumn === col.keyName;
                return (
                  <TableCell 
                    key={col.keyName}
                    onClick={(e) => {
                      // Don't trigger sort if clicking on filter button
                      if (e.target.closest('button')) return;
                      handleSort(col.keyName);
                    }}
                    sx={{ 
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'white !important',
                      padding: '8px',
                      background: 'white',
                      zIndex: 10,
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>{col.title}</span>
                      {isSorted && (
                        sortDirection === 'asc' ? 
                          <ArrowUpwardIcon fontSize="small" style={{ color: '#1976d2' }} /> : 
                          <ArrowDownwardIcon fontSize="small" style={{ color: '#1976d2' }} />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent sort when clicking filter
                          setCurrentFilterColumn(col);
                          setFilterAnchorEl(e.currentTarget);
                        }}
                        style={{
                          background: hasFilter ? '#1976d2' : 'transparent',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: hasFilter ? 'white' : '#666'
                        }}
                        title="Filter"
                      >
                        🔍
                      </button>
                    </div>
                  </TableCell>
                );
              })}
              <TableCell 
                key={"__edit__"}
                sx={{ 
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'white !important',
                  zIndex: 15,
                  padding: '8px',
                  background: 'white'
                }}
              >
                {canDo("createAnnotation", {projectId}) ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EditModal annotationsFields={annotationFields} />
                  </div>
                ) : null}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Virtual row before (for scroll) */}
            <TableRow key="group_before" style={beforeRowStyle}></TableRow>
            
            {/* Visible rows */}
            {visibleRowsData.map((rowData, idx_in_subarray) => {
              const actualRowIdx = scrollIdx + idx_in_subarray;
              return (
                <MemoizedTableRow
                  key={rowData.ann.id}
                  ann={rowData.ann}
                  annDisplayData={rowData.displayData}
                  isSelected={rowData.isSelected}
                  columnsToDisplay={columnsToDisplay}
                  onClick={(e) => handleRowClickDeferred(e, rowData.ann.id, actualRowIdx)}
                  rowHeight={ROW_HEIGHT_PIXELS}
                  onMoveTo={handleMoveTo}
                  editingCell={editingCell}
                  editDraftValue={editDraftValue}
                  canEditCells={canEditAnnotationFields}
                  getFieldDefForColumn={getFieldDefForAnnotation}
                  onCellDoubleClick={handleCellDoubleClick}
                  onCellFieldChange={setEditDraftValue}
                  onCellEditEnter={commitCellEdit}
                  onCellEditEscape={cancelCellEdit}
                />
              );
            })}
            
            {/* Virtual row after (for scroll) */}
            <TableRow key="group_after" style={afterRowStyle}></TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Filter popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => {
          setFilterAnchorEl(null);
          setCurrentFilterColumn(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {currentFilterColumn && (
          <Box sx={{ p: 2, minWidth: 300, maxWidth: 400, maxHeight: 500, overflow: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Filter: {currentFilterColumn.title}
            </Typography>
            
            {(() => {
              const isTextInfoField = currentFilterColumn.keyName === '__created_by_email__' ||
                                      currentFilterColumn.keyName === 'message';
              const useCheckboxFilter = (currentFilterColumn.field_type === 'select' || currentFilterColumn.keyName === '__geometry__') && !isTextInfoField;
              const useNumberFilter = currentFilterColumn.field_type === 'number' && !isTextInfoField;
              const useTextColumnWithCheckboxList =
                (currentFilterColumn.field_type === 'text' || currentFilterColumn.field_type === 'textarea') &&
                !isTextInfoField &&
                currentFilterColumn.keyName !== '__geometry__';

              // Number filter
              if (useNumberFilter) {
                const colKey = currentFilterColumn.keyName;
                const numberFilter = readNumberColumnFilterState(columnFilters[colKey]);
                const uniqueColumnValues = columnUniqueValues[colKey] ?? [];
                const hasPartialValueList =
                  numberFilter.allowedValues.size > 0 &&
                  numberFilter.allowedValues.size < uniqueColumnValues.length;
                const excludesAllValues = numberFilter.excludeAllColumnValues;
                const checkboxListAllChecked =
                  !excludesAllValues &&
                  (!hasPartialValueList ||
                    uniqueColumnValues.every((v) =>
                      numberFilter.allowedValues.has(String(v))
                    ));
                const checkboxListIndeterminate =
                  !excludesAllValues &&
                  hasPartialValueList;
                return (
                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Filter by comparison:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Select
                          size="small"
                          value={numberFilter.operator || '>'}
                          onChange={(e) => {
                            setColumnFilters(prev => {
                              const numberPart = readNumberColumnFilterState(prev[colKey]);
                              return {
                                ...prev,
                                [colKey]: {
                                  type: 'number',
                                  value: {
                                    ...numberPart,
                                    operator: e.target.value,
                                  },
                                },
                              };
                            });
                          }}
                          sx={{ minWidth: 80 }}
                        >
                          <MenuItem value=">">&gt;</MenuItem>
                          <MenuItem value="<">&lt;</MenuItem>
                          <MenuItem value=">=">&ge;</MenuItem>
                          <MenuItem value="<=">&le;</MenuItem>
                          <MenuItem value="=">=</MenuItem>
                        </Select>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Value"
                          value={numberFilter.compareValue || ''}
                          onChange={(e) => {
                            setColumnFilters(prev => {
                              const numberPart = readNumberColumnFilterState(prev[colKey]);
                              return {
                                ...prev,
                                [colKey]: {
                                  type: 'number',
                                  value: {
                                    ...numberPart,
                                    compareValue: e.target.value,
                                  },
                                },
                              };
                            });
                          }}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Box>
                    
                    <ColumnFilterCheckboxListPanel
                      uniqueColumnValues={uniqueColumnValues}
                      allChecked={checkboxListAllChecked}
                      indeterminate={checkboxListIndeterminate}
                      onAllChange={(e) =>
                        setColumnFilters((prev) =>
                          setColumnFilterAllValues(prev, colKey, 'number', e.target.checked)
                        )
                      }
                      getIsChecked={(value) =>
                        excludesAllValues
                          ? false
                          : hasPartialValueList
                            ? numberFilter.allowedValues.has(String(value))
                            : true
                      }
                      onRowToggle={(value, checked) =>
                        setColumnFilters((prev) =>
                          setColumnFilterOneValue(
                            prev,
                            colKey,
                            'number',
                            value,
                            checked,
                            uniqueColumnValues
                          )
                        )
                      }
                    />
                    
                    {((numberFilter.compareValue && numberFilter.compareValue !== '') ||
                      numberFilter.allowedValues.size > 0 ||
                      numberFilter.excludeAllColumnValues) && (
                      <button
                        type="button"
                        onClick={() => {
                          setColumnFilters(prev => {
                            const newFilters = { ...prev };
                            delete newFilters[currentFilterColumn.keyName];
                            return newFilters;
                          });
                        }}
                        style={{ marginTop: '8px', ...FILTER_POPOVER_CLEAR_BTN_STYLE }}
                      >
                        Clear filter
                      </button>
                    )}
                  </Box>
                );
              }
              
              // Checkbox filter (select/geometry)
              if (useCheckboxFilter) {
                const colKey = currentFilterColumn.keyName;
                const uniqueColumnValues = columnUniqueValues[colKey] ?? [];
                const entry = columnFilters[colKey];
                const valueListIsActive = entry?.type === 'checkbox';
                const allowedValues =
                  valueListIsActive ? (entry.allowedValues || entry.value || new Set()) : new Set();
                const excludesAllValues = !!(
                  entry?.excludeAllColumnValues || entry?.emptyDiscrete
                );
                const hasPartialValueList =
                  valueListIsActive &&
                  allowedValues.size > 0 &&
                  allowedValues.size < uniqueColumnValues.length;
                const checkboxListAllChecked =
                  !excludesAllValues &&
                  (!valueListIsActive ||
                    uniqueColumnValues.every((v) => allowedValues.has(String(v))));
                const checkboxListIndeterminate =
                  !excludesAllValues && hasPartialValueList;
                return (
                  <Box>
                    <TextField
                      size="small"
                      placeholder="Search in this column..."
                      fullWidth
                      sx={{ mb: 2 }}
                      value={columnFilters[currentFilterColumn.keyName]?.type === 'text' ? columnFilters[currentFilterColumn.keyName].value : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setColumnFilters(prev => {
                          if (!value) {
                            const newFilters = { ...prev };
                            if (newFilters[currentFilterColumn.keyName]?.type === 'text') {
                              delete newFilters[currentFilterColumn.keyName];
                            }
                            return newFilters;
                          }
                          return {
                            ...prev,
                            [currentFilterColumn.keyName]: {
                              type: 'text',
                              value: value
                            }
                          };
                        });
                      }}
                    />
                    {columnFilters[currentFilterColumn.keyName]?.type === 'text' && columnFilters[currentFilterColumn.keyName]?.value && (
                      <button
                        onClick={() => {
                          setColumnFilters(prev => {
                            const newFilters = { ...prev };
                            delete newFilters[currentFilterColumn.keyName];
                            return newFilters;
                          });
                        }}
                        style={{
                          marginBottom: '16px',
                          padding: '4px 8px',
                          background: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Clear filter
                      </button>
                    )}
                    
                    <ColumnFilterCheckboxListPanel
                      uniqueColumnValues={uniqueColumnValues}
                      allChecked={checkboxListAllChecked}
                      indeterminate={checkboxListIndeterminate}
                      onAllChange={(e) =>
                        setColumnFilters((prev) =>
                          setColumnFilterAllValues(prev, colKey, 'checkbox', e.target.checked)
                        )
                      }
                      getIsChecked={(value) =>
                        excludesAllValues
                          ? false
                          : valueListIsActive
                            ? allowedValues.has(String(value))
                            : true
                      }
                      onRowToggle={(value, checked) =>
                        setColumnFilters((prev) =>
                          setColumnFilterOneValue(
                            prev,
                            colKey,
                            'checkbox',
                            value,
                            checked,
                            uniqueColumnValues
                          )
                        )
                      }
                    />
                    {(columnFilters[currentFilterColumn.keyName]?.type === 'checkbox' &&
                      (columnFilters[currentFilterColumn.keyName]?.allowedValues?.size > 0 ||
                        columnFilters[currentFilterColumn.keyName]?.value?.size > 0 ||
                        columnFilters[currentFilterColumn.keyName]?.excludeAllColumnValues ||
                        columnFilters[currentFilterColumn.keyName]?.emptyDiscrete)) && (
                      <button
                        type="button"
                        onClick={() => {
                          setColumnFilters(prev => {
                            const newFilters = { ...prev };
                            delete newFilters[currentFilterColumn.keyName];
                            return newFilters;
                          });
                        }}
                        style={{ marginTop: '8px', ...FILTER_POPOVER_CLEAR_BTN_STYLE }}
                      >
                        Clear filter
                      </button>
                    )}
                  </Box>
                );
              }
              
              if (useTextColumnWithCheckboxList) {
                const colKey = currentFilterColumn.keyName;
                return (
                  <TextColumnWithCheckboxListFilter
                    columnKey={colKey}
                    columnFilters={columnFilters}
                    setColumnFilters={setColumnFilters}
                    uniqueColumnValues={columnUniqueValues[colKey] ?? []}
                  />
                );
              }

              // Text filter (default: search only — e.g. message, long text)
              return (
                <Box>
                  <TextField
                    size="small"
                    placeholder="Search in this column..."
                    fullWidth
                    value={columnFilters[currentFilterColumn.keyName]?.value || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setColumnFilters(prev => {
                        if (!value) {
                          const newFilters = { ...prev };
                          delete newFilters[currentFilterColumn.keyName];
                          return newFilters;
                        }
                        return {
                          ...prev,
                          [currentFilterColumn.keyName]: {
                            type: 'text',
                            value: value
                          }
                        };
                      });
                    }}
                  />
                  {columnFilters[currentFilterColumn.keyName]?.value && (
                    <button
                      onClick={() => {
                        setColumnFilters(prev => {
                          const newFilters = { ...prev };
                          delete newFilters[currentFilterColumn.keyName];
                          return newFilters;
                        });
                      }}
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        background: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Clear filter
                    </button>
                  )}
                </Box>
              );
            })()}
          </Box>
        )}
      </Popover>
      </>
      )}
    </div>
  );
}
  
export default AnnotationTablePage
